import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { Doc as YDoc } from 'yjs';

import { SnapshotVersion } from '@tiptap-pro/extension-snapshot';

import { TiptapCollabProvider } from '@tiptap-pro/provider';

import { CircularProgress, Dialog, IconButton, Divider } from '@mui/material';
import { Close } from '@mui/icons-material';

import { useDocumentName } from 'containers/Tiptap/hooks/use-document-variables';
import { useReadOnlyPagesLayout } from 'containers/Tiptap/hooks/use-read-only-pages-layout';
import { useReadOnlyPreviewEditor } from 'containers/Tiptap/hooks/use-read-only-preview-editor';
import { requestVersionPreview, VersionPreview, watchVersionPreview } from 'containers/Tiptap/lib/version-preview';
import { DocumentViewMode } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { ReadOnlyEditorContent } from 'containers/Tiptap/components/tiptap-templates/styles';
import { TocProvider, useToc } from 'containers/Tiptap/components/tiptap-node/toc-node/context/toc-context';

import VersionItem from './VersionItem';
import {
  ModalContent,
  VersionListPanel,
  PreviewArea,
  PreviewLoading,
  PreviewPanel,
  ModalHeader,
  ModalTitle,
  VersionsGroup,
  ButtonGroup,
  ActionButton,
} from './styles';
import { getVersionName } from './utils';

interface VersionHistoryModalProps {
  versions: SnapshotVersion[];
  isOpen: boolean;
  onClose: () => void;
  onRevert: (version: number) => void;
  provider: TiptapCollabProvider;
  ydoc: YDoc | null | undefined;
  viewMode: DocumentViewMode;
  hasUnpublishedChanges: boolean;
}

const VersionHistoryModalContent = observer(
  ({ versions, isOpen, onClose, onRevert, provider, ydoc, viewMode, hasUnpublishedChanges }: VersionHistoryModalProps) => {
    const titleId = React.useId();
    const initialVersion = versions.at(-1)?.version;
    const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
    const [preview, setPreview] = useState<VersionPreview | null>(null);
    const selectedPreview = preview?.version === currentVersionId ? preview : null;
    const showLoading = !selectedPreview && versions.length > 0;
    const isLatestVersion = versions && versions.length > 0 ? currentVersionId === versions.at(-1)?.version : false;

    const reversedVersions = useMemo(() => versions.slice().reverse(), [versions]);

    const { setTocContent } = useToc();
    const documentName = useDocumentName();

    // Built once per document and view mode: destroying a Pages editor breaks the main editor's header
    // and footer editing (https://github.com/ueberdosis/tiptap/issues/8552).
    const {
      editor: previewEditor,
      contentApplied,
      markContentApplied,
    } = useReadOnlyPreviewEditor(setTocContent, ydoc, [ydoc, viewMode]);

    useReadOnlyPagesLayout({
      editor: previewEditor,
      settings: selectedPreview?.settings,
      contentApplied,
      variables: { documentName, documentVersion: String(currentVersionId ?? initialVersion ?? '') },
    });

    const handleVersionChange = useCallback(
      (newVersion: number) => {
        setCurrentVersionId(newVersion);
        requestVersionPreview(provider, newVersion);
      },
      [provider]
    );

    useEffect(() => {
      if (isOpen && currentVersionId === null && versions.length > 0 && initialVersion !== undefined) {
        setCurrentVersionId(initialVersion);
        requestVersionPreview(provider, initialVersion);
      }
    }, [currentVersionId, versions, isOpen, provider, initialVersion]);

    useEffect(() => {
      if (isOpen) {
        return watchVersionPreview(provider, (nextPreview) => {
          if (previewEditor && nextPreview.version === currentVersionId) {
            previewEditor.commands.setContent(nextPreview.content);
            setPreview(nextPreview);
            markContentApplied();
          }
        });
      }
      return undefined;
    }, [isOpen, provider, previewEditor, markContentApplied, currentVersionId]);

    const handleClose = useCallback((): void => {
      setCurrentVersionId(null);
      setPreview(null);
      onClose();
    }, [onClose]);

    const handleRevert = useCallback(() => {
      if (!selectedPreview) return;
      setCurrentVersionId(null);
      setPreview(null);
      onRevert(selectedPreview.version);
      onClose();
    }, [onRevert, selectedPreview, onClose]);

    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        slotProps={{ paper: { sx: { maxWidth: '1100px', height: '80vh', borderRadius: '12px', boxShadow: 'none' } } }}
        aria-labelledby={titleId}
      >
        <ModalHeader>
          <ModalTitle id={titleId}>Version history</ModalTitle>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <Close />
          </IconButton>
        </ModalHeader>
        <Divider />
        <ModalContent>
          <VersionListPanel>
            <VersionsGroup>
              {reversedVersions.map(({ date, version, name }) => (
                <VersionItem
                  date={date}
                  key={`version_item_${version}`}
                  isActive={currentVersionId === version}
                  title={name || getVersionName({ version })}
                  onClick={() => handleVersionChange(version)}
                  isLatestVersion={initialVersion === version}
                />
              ))}
            </VersionsGroup>
            <ButtonGroup>
              <ActionButton type="button" onClick={handleClose}>
                Close
              </ActionButton>
              <ActionButton $isPrimary type="button" disabled={!selectedPreview || (isLatestVersion && !hasUnpublishedChanges)} onClick={handleRevert}>
                Restore
              </ActionButton>
            </ButtonGroup>
          </VersionListPanel>
          <PreviewArea>
            <PreviewPanel $hidden={showLoading}>
              {previewEditor && <ReadOnlyEditorContent editor={previewEditor} className="simple-editor-content" />}
            </PreviewPanel>
            {showLoading && (
              <PreviewLoading>
                <CircularProgress size={28} />
              </PreviewLoading>
            )}
          </PreviewArea>
        </ModalContent>
      </Dialog>
    );
  }
);

export const VersionHistoryModal = memo((props: VersionHistoryModalProps) => (
  <TocProvider>
    <VersionHistoryModalContent {...props} />
  </TocProvider>
));

export default VersionHistoryModal;
